import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phase } from '@/types/user';
import { Check, Lock, ChevronDown, Info } from 'lucide-react';

interface PhaseTrackerCardsProps {
  currentPhase: Phase;
  onPhaseClick?: (phase: Phase) => void;
  isAdmin?: boolean;
}

const phases = [
  { phase: 1, label: 'Welcome' },
  { phase: 2, label: 'Fast Track' },
  { phase: 3, label: 'Clarity' },
  { phase: 4, label: 'Action' },
  { phase: 5, label: 'First Deal' },
];

const PHASE_DESCRIPTIONS: Record<number, string> = {
  1: 'You are in the onboarding phase.',
  2: 'You are in the Fast Track training phase.',
  3: 'You are defining your investment criteria.',
  4: 'You are in the deal execution phase.',
  5: "You've closed your first deal. Celebrate!",
};

// Shown in the expanded journey view
const PHASE_JOURNEY_DESCRIPTIONS: Record<number, string> = {
  1: 'Get oriented to the First Deal program and community. Sign your First Deal Declaration (a 60-second commitment statement), introduce yourself in the group, and attend your first call.',
  2: 'Build your foundation through 5 weeks of intensive real estate education. Attend weekly calls, complete course modules, and submit your program feedback to unlock Phase 3.',
  3: 'Define your investment strategy and get clarity on exactly what type of deal you are looking for. Book a clarity call with a mentor to sharpen your criteria before moving to action.',
  4: 'Put your plan into action. Analyze properties, submit offers, and track your weekly KPIs. This is where your first deal gets done.',
  5: 'You closed your first deal. Reflect on everything you learned, celebrate the milestone, and set your sights on the next one.',
};

const PHASE_STATUS: (phase: number, current: number) => { label: string; className: string } = (phase, current) => {
  if (phase < current) return { label: 'Completed', className: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
  if (phase === current) return { label: 'In Progress', className: 'text-gold bg-gold/10 border-gold/30' };
  return { label: 'Locked', className: 'text-muted-foreground bg-muted border-border' };
};

export const PhasePill = ({ currentPhase, onPhaseClick, isAdmin }: PhaseTrackerCardsProps) => {
  const [showLockedCallout, setShowLockedCallout] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (showLockedCallout) {
      dismissTimer.current = setTimeout(() => setShowLockedCallout(false), 3000);
    }
    return () => { if (dismissTimer.current) clearTimeout(dismissTimer.current); };
  }, [showLockedCallout]);

  const handleClick = (phase: Phase) => {
    if (!isAdmin && phase > currentPhase) { setShowLockedCallout(true); return; }
    onPhaseClick?.(phase);
  };

  return (
    <div className="relative w-fit min-w-max">
      <div className="flex items-center gap-3 bg-card border border-border rounded-2xl px-4 py-2.5">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground shrink-0">Phase</span>
        <div className="w-px h-3.5 bg-border shrink-0" />
        <div className="flex items-center">
          {phases.map(({ phase, label }, idx) => {
            const isComplete = phase < currentPhase;
            const isCurrent = phase === currentPhase;
            const isClickable = isAdmin || phase <= currentPhase;
            return (
              <div key={phase} className="flex items-center">
                {idx > 0 && (
                  <div className={`w-4 h-px shrink-0 ${isComplete || isCurrent ? 'bg-gold/50' : 'bg-border'}`} />
                )}
                {isCurrent ? (
                  <button
                    onClick={() => handleClick(phase as Phase)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full border-2 border-gold text-gold font-semibold text-xs whitespace-nowrap bg-gold/5 hover:bg-gold/10 transition-colors"
                  >
                    <span>{phase}</span>
                    <span>{label}</span>
                  </button>
                ) : isComplete ? (
                  <button
                    onClick={() => handleClick(phase as Phase)}
                    className={`w-6 h-6 rounded-full gradient-gold-radial flex items-center justify-center shrink-0 shadow-sm transition-opacity ${isClickable ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                    title={label}
                  >
                    <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleClick(phase as Phase)}
                    className={`w-6 h-6 rounded-full border border-border bg-background flex items-center justify-center text-xs font-medium text-muted-foreground shrink-0 transition-colors ${isClickable ? 'hover:border-muted-foreground/40 cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                    title={label}
                  >
                    {phase}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <AnimatePresence>
        {showLockedCallout && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full mt-2 right-0 z-10 w-64 flex items-start gap-2.5 rounded-xl border border-gold/40 bg-gold/10 px-3.5 py-3 shadow-sm"
          >
            <Info className="h-4 w-4 text-gold shrink-0 mt-0.5" />
            <p className="text-xs text-foreground font-medium leading-snug">
              Complete your current phase to unlock the next one.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PhaseTrackerCards = ({ currentPhase, onPhaseClick, isAdmin }: PhaseTrackerCardsProps) => {
  const [expanded, setExpanded] = useState(false);
  const [showLockedCallout, setShowLockedCallout] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentLabel = phases.find(p => p.phase === currentPhase)?.label ?? '';
  const currentDescription = PHASE_DESCRIPTIONS[currentPhase] ?? '';

  // Auto-dismiss the locked callout after 5 seconds
  useEffect(() => {
    if (showLockedCallout) {
      dismissTimer.current = setTimeout(() => setShowLockedCallout(false), 5000);
    }
    return () => { if (dismissTimer.current) clearTimeout(dismissTimer.current); };
  }, [showLockedCallout]);

  const handleClick = (phase: Phase) => {
    if (!isAdmin && phase > currentPhase) {
      setShowLockedCallout(true);
      return;
    }
    onPhaseClick?.(phase);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full mb-10 bg-card border border-border rounded-2xl overflow-hidden"
    >
      {/* Top row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 px-6 py-5">

        {/* Left: Current phase identity */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full gradient-gold-radial flex items-center justify-center text-primary-foreground font-bold text-lg shrink-0 shadow-gold">
            {currentPhase}
          </div>
          <div>
            <p className="font-semibold text-foreground leading-snug">Current Phase: {currentLabel}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{currentDescription}</p>
          </div>
        </div>

        {/* Right: Mini step tracker + View Full Journey */}
        <div className="flex items-center gap-3 sm:shrink-0">

          {/* Steps */}
          <div className="flex items-center">
            {phases.map(({ phase, label }, idx) => {
              const isComplete = phase < currentPhase;
              const isCurrent = phase === currentPhase;
              const isFuture = phase > currentPhase;
              const isClickable = isAdmin || phase <= currentPhase;

              return (
                <div key={phase} className="flex items-center">
                  {idx > 0 && (
                    <div className={`w-4 h-px shrink-0 ${isComplete || isCurrent ? 'bg-gold/50' : 'bg-border'}`} />
                  )}

                  {isCurrent ? (
                    <button
                      onClick={() => handleClick(phase as Phase)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-gold text-gold font-semibold text-xs whitespace-nowrap bg-gold/5 hover:bg-gold/10 transition-colors"
                    >
                      <span>{phase}</span>
                      <span>{label}</span>
                    </button>
                  ) : isComplete ? (
                    <button
                      onClick={() => handleClick(phase as Phase)}
                      className={`w-7 h-7 rounded-full gradient-gold-radial flex items-center justify-center shrink-0 shadow-sm transition-opacity ${isClickable ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                      title={label}
                    >
                      <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleClick(phase as Phase)}
                      className={`w-7 h-7 rounded-full border border-border bg-background flex items-center justify-center text-xs font-medium text-muted-foreground shrink-0 transition-colors ${isClickable ? 'hover:border-muted-foreground/40 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                      title={isFuture && !isAdmin ? 'Locked' : label}
                    >
                      {phase}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Divider + View Full Journey toggle */}
          <div className="w-px h-5 bg-border shrink-0" />
          <button
            onClick={() => setExpanded(prev => !prev)}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            {expanded ? 'Hide Journey' : 'View Full Journey'}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

      </div>

      {/* Locked phase callout */}
      <AnimatePresence>
        {showLockedCallout && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="mx-6 mb-0 pb-5"
          >
            <div className="flex items-start gap-3 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3.5">
              <Info className="h-4 w-4 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-foreground font-medium leading-snug">
                Complete all the actions in your current phase to unlock the next one. Keep going -- you're making progress!
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded journey view */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-border px-6 py-5 space-y-0">
              {phases.map(({ phase, label }, idx) => {
                const isComplete = phase < currentPhase;
                const isCurrent = phase === currentPhase;
                const isFuture = !isAdmin && phase > currentPhase;
                const status = PHASE_STATUS(phase, currentPhase);
                const isClickable = isAdmin || phase <= currentPhase;
                const isLast = idx === phases.length - 1;

                return (
                  <motion.div
                    key={phase}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                    className="flex gap-4"
                  >
                    {/* Vertical connector + circle */}
                    <div className="flex flex-col items-center shrink-0">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center border-2 shrink-0 transition-all ${
                          isComplete
                            ? 'gradient-gold-radial border-transparent shadow-sm'
                            : isCurrent
                            ? 'bg-card border-gold shadow-sm'
                            : 'bg-muted border-border'
                        }`}
                      >
                        {isComplete ? (
                          <motion.div
                            initial={{ scale: 0.4, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', duration: 0.4, bounce: 0.35 }}
                            className="flex items-center justify-center"
                          >
                            <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
                          </motion.div>
                        ) : isFuture ? (
                          <Lock className="h-3 w-3 text-muted-foreground/60" />
                        ) : (
                          <span className="text-xs font-bold text-gold">{phase}</span>
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-px flex-1 my-1 min-h-[20px] ${isComplete ? 'bg-gold/30' : 'bg-border'}`} />
                      )}
                    </div>

                    {/* Content */}
                    <div className={`flex-1 pb-5 ${isLast ? 'pb-1' : ''}`}>
                      <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                        <button
                          onClick={() => isClickable && handleClick(phase as Phase)}
                          className={`font-semibold text-sm leading-snug transition-colors ${
                            isComplete
                              ? 'text-foreground hover:text-gold cursor-pointer'
                              : isCurrent
                              ? 'text-foreground cursor-pointer'
                              : 'text-muted-foreground cursor-not-allowed'
                          }`}
                          disabled={!isClickable}
                        >
                          Phase {phase}: {label}
                        </button>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${status.className}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {PHASE_JOURNEY_DESCRIPTIONS[phase]}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PhaseTrackerCards;
